import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';

import { type Merchant } from '@trendiolive/trendio-selfcare-ui-kit';

import { merchantAttachStarted, sessionEnded } from 'app/actions/sessionLifecycle';

import { MerchantSettings } from 'model/classes/MerchantSettings';
import { MerchantState } from 'model/types/merchant';
import { CreatorTier, CreatorTierConnectionResult, CreatorTierInput, CreatorTierSetCreateResult } from 'model/types/creator-tier';
import { tierFromMerchantResponse } from 'model/mappers/tikTokShopTier';

import { GET_MERCHANT_BASIC_INFO } from 'queries/get_merchant_basic_info';
import { GET_CREATOR_TIERS } from 'queries/get_creator_tiers';
import { CREATOR_TIER_SET_CREATE } from 'queries/creator_tier_set_create';

import { hasTiktokShopConnection as toHasTiktokShopConnection, mapShopPerformanceScore } from 'model/mappers/shopPerformance';

import { authenticatedPost } from 'utils/axios';
import { throwOnGraphQLErrors } from 'utils/graphql';

export const initialState: MerchantState = {
  displayDashboard: true,
  displayMessageCenter: false,
  hasCampaignPlan: false,
  merchant: null,
  sps: null,
  hasTiktokShopConnection: false
};

export const merchantSlice = createSlice({
  name: 'merchant',
  initialState,
  reducers: {
    setBasicInfo(state, action: PayloadAction<MerchantState>) {
      state.merchant = action.payload.merchant;
      state.ttsPermissionsGranted = action.payload.merchant?.onboarding?.tiktokSellerPortalPermissionsGranted ?? false;
      state.displayMessageCenter = new MerchantSettings(action.payload.merchant?.settingsObj).getSettingsBooleanAtPath(
        MerchantSettings.PATHS.dm_auto_reply
      );
      state.tiktokPlan = action.payload.tiktokPlan;
      state.youtubePlan = action.payload.youtubePlan;
      state.ttsTierRestrictions = tierFromMerchantResponse(action.payload.merchant);
      state.sps = action.payload.sps;
      state.hasTiktokShopConnection = action.payload.hasTiktokShopConnection;
      state.hasCampaignPlan = action.payload.hasCampaignPlan ?? false;
    },
    grantTTSPermissions(state, action: PayloadAction<Pick<MerchantState, 'merchant' | 'ttsPermissionsGranted'>>) {
      state.ttsPermissionsGranted = action.payload.ttsPermissionsGranted;
    },
    setDisplayDashboard(state, action: PayloadAction<boolean>) {
      state.displayDashboard = action.payload;
    },
    setTTSCurrentPeriodData(state, action: PayloadAction<Pick<MerchantState, 'merchant'>>) {
      if (state.merchant !== null) {
        state.ttsTierRestrictions = tierFromMerchantResponse(action.payload.merchant);
        state.merchant.settingsObj = action.payload.merchant?.settingsObj;
        if (state.merchant.onboarding) state.merchant.onboarding = { ...state.merchant.onboarding, ...action.payload.merchant?.onboarding };
        if (state.tiktokPlan) state.tiktokPlan = { ...state.tiktokPlan, ...action.payload.merchant?.plan };
      }
    },
    setMerchantSettingsObj(state, action: PayloadAction<Merchant['settingsObj']>) {
      if (state.merchant !== null && action.payload != null) {
        state.merchant.settingsObj = action.payload;
        state.displayMessageCenter = new MerchantSettings(action.payload).getSettingsBooleanAtPath(MerchantSettings.PATHS.dm_auto_reply);
      }
    },
    setCreatorTiers(state, action: PayloadAction<CreatorTier[]>) {
      state.creatorTiers = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(sessionEnded, () => initialState).addCase(merchantAttachStarted, () => initialState);
  }
});

export const { grantTTSPermissions, setBasicInfo, setDisplayDashboard, setTTSCurrentPeriodData, setMerchantSettingsObj, setCreatorTiers } =
  merchantSlice.actions;

// use cases
export async function queryMerchantInfo(): Promise<MerchantState> {
  const result = await authenticatedPost({ query: GET_MERCHANT_BASIC_INFO });
  // A GraphQL error response is `{"errors": [...]}` with no `data` key at all. Resolving with the
  // no-merchant state instead of throwing a `TypeError` lets the caller classify the failure.
  if (result.data?.merchant) {
    const state: MerchantState = {
      merchant: result.data.merchant,
      tiktokPlan: result.data.merchant.tiktokPlan,
      youtubePlan: result.data.merchant.youtubePlan,
      sps: mapShopPerformanceScore(result.data.merchant.tiktokShopConnections),
      hasTiktokShopConnection: toHasTiktokShopConnection(result.data.merchant.tiktokShopConnections),
      hasCampaignPlan: !!result.data.campaignPlans?.edges?.[0]?.node?.id
    };
    return state;
  }
  return { merchant: null, sps: null, hasTiktokShopConnection: false };
}

export async function loadMerchantInfo(dispatch: Dispatch): Promise<void> {
  const result = await queryMerchantInfo();
  if (!result.merchant) throw new Error('The merchant could not be loaded');
  dispatch(setBasicInfo(result));
}

export async function queryCreatorTiers(abortController?: AbortController): Promise<CreatorTier[]> {
  const result = await authenticatedPost({ query: GET_CREATOR_TIERS, abortController });
  throwOnGraphQLErrors(result);
  const data = result.data as CreatorTierConnectionResult;
  return data.creatorTiers?.edges.map((edge) => edge.node) ?? [];
}

export async function saveCreatorTierSet(tiers: CreatorTierInput[]): Promise<CreatorTier[]> {
  const result = await authenticatedPost({
    query: CREATOR_TIER_SET_CREATE,
    vars: { input: { tiers } }
  });
  const data = result.data as CreatorTierSetCreateResult;
  return data.creatorTierSetCreate?.creatorTiers ?? [];
}
